// Footer: the edge-to-edge engraved "nRg Praxis" with a holographic foil, plus phone accordions.
// Foil maths ported from vercel-labs/vgpu examples/holographic-card/shader.wgsl (MIT,
// Copyright (c) Vercel, Inc.): etched contours, grating diffraction, pearl sweep, glint, sparkle.
// Home draws into the shared renderer (scissored to the word); inner pages have no WebGL, so the
// word owns the page's only canvas. Both run on gsap.ticker and pause offscreen.
import * as THREE from 'three';
import { gsap } from 'gsap';
import { getWebGL, setFooterPass } from '../core/webgl';

// Look, tuned in demo/holo-vgpu/footer.html (slider panel there).
const TUNE = {
  textDim: 0.32,   // light theme: resting letter tone (0 = paper, 1 = black)
  abyss: 0.7,      // dark theme: floor as a shade of the theme black (shadows go to pure black)
  depth: 9,        // engraving depth, css px
  shadow: 0.85,    // shadow on the top-left walls
  bevel: 0.45,     // bottom-right walls (light: catch light; dark: rise back to theme black)
  lip: 0.35,       // light theme: thin highlight on the outer bottom-right edge
  lines: 0.22,     // engraved contour lines at rest
  foil: 1.0,       // rainbow strength under the light
  saturation: 1.0,
  glint: 0.3,
  sparkle: 0.25,
  lightSize: 1.7,  // spotlight size / sweep length
  idle: 0.8,       // drifting light when the cursor is away (0 = off)
  idleSpeed: 0.12, // one drift loop ≈ 6.3 / idleSpeed s
};

const frag = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uMask;
uniform vec2 uRes;
uniform vec2 uPointer;
uniform float uHover, uLight;
uniform vec3 uBg, uInk;
uniform float uDim, uAbyss, uDepth, uShadow, uBevel, uLip, uLines, uFoil, uSat, uGlint, uSparkle, uSize;

float stroke(float d, float w, float aa) { return 1.0 - smoothstep(w, w + aa, abs(d)); }

vec3 pearl(float t) {
  vec3 c = vec3(0.55, 0.52, 0.64) + vec3(0.43, 0.40, 0.34) * cos(6.2831853 * (t + vec3(0.05, 0.38, 0.63)));
  return clamp(mix(vec3(dot(c, vec3(0.333))), c, uSat), 0.0, 1.0);
}

vec3 wavelengthColor(float w) {
  vec3 r = (vec3(w) - vec3(0.610, 0.545, 0.460)) / vec3(0.045, 0.038, 0.032);
  float vis = smoothstep(0.380, 0.410, w) * (1.0 - smoothstep(0.700, 0.780, w));
  return exp(-0.5 * r * r) * vis;
}

// Grating diffraction (Stam, GPU Gems ch. 8).
vec3 diffraction(vec2 across, vec2 lv, float spacing) {
  float path = spacing * abs(dot(lv, across));
  float along = dot(lv, vec2(-across.y, across.x));
  float env = exp(-along * along / 0.36);
  vec3 r = vec3(0.0);
  for (int o = 1; o <= 3; o++) { float m = float(o); r += wavelengthColor(path / m) / (m * m); }
  return r * env;
}

float grain(vec2 p) {
  vec2 q = floor(abs(p) * 2400.0);
  return fract(sin(dot(q, vec2(12.9898, 78.233))) * 43758.5453) - 0.5;
}

float etchedPhase(vec2 p) {
  vec2 warp = vec2(sin(p.y * 7.0 + sin(p.x * 4.0)) * 0.085, sin(p.x * 6.0 - p.y * 3.0) * 0.07);
  vec2 q = p + warp - uPointer * 0.15;
  float r = length(q * vec2(1.0, 0.76));
  return r * 142.0 + sin(atan(q.y, q.x) * 3.0 + r * 8.0) * 1.7;
}

// How much "outside" lies along a direction: the walls of the cut, softened over uDepth px.
float wall(vec2 dir) {
  vec2 px = dir / uRes;
  float s = 0.0;
  for (int i = 1; i <= 6; i++) s += (1.0 - texture2D(uMask, vUv + px * uDepth * float(i) / 6.0).r) * (1.0 - float(i) / 7.0);
  return s / 3.0;
}

void main() {
  float mask = texture2D(uMask, vUv).r;
  if (mask < 0.003) {
    vec2 px = vec2(-1.0, 1.0) / uRes;
    float edge = max(texture2D(uMask, vUv + px * 1.5).r, texture2D(uMask, vUv + px * 3.0).r * 0.5);
    float a = edge * uLip * uLight; // light theme only; dark stays all black
    gl_FragColor = vec4(vec3(a), a);
    return;
  }
  vec2 p = (vUv - 0.5) * vec2(uRes.x / uRes.y, -1.0) * 1.8;
  float hover = clamp(uHover, 0.0, 1.0);

  vec2 delta = p - uPointer;
  float sweep = delta.x * 0.72 + delta.y * 0.52 + sin(p.y * 4.0 + p.x * 3.0) * 0.08;
  float band = exp(-pow(sweep / (0.36 * uSize), 2.0));
  float glint = exp(-pow(sweep / (0.085 * uSize), 2.0));
  vec2 d = delta * vec2(0.6, 0.72) / uSize;
  float spot = exp(-dot(d, d) * 2.6);
  float light = band * spot * hover;
  vec3 L = normalize(vec3(uPointer, 1.2) - vec3(p, 0.0));
  vec2 lv = (L + vec3(0.0, 0.0, 1.0)).xy;

  float contour = etchedPhase(p);
  vec2 g = vec2(dFdx(contour), dFdy(contour));
  vec2 across = g / max(length(g), 1e-8);
  float lines = stroke(sin(contour), 0.06, min(fwidth(contour), 1.0));
  float noise = grain(p + 2.0);

  // Dark: shades of black only (floor below theme black, lines cut darker still).
  vec3 floorCol = uLight > 0.5 ? mix(uBg, uInk, uDim) : uBg * uAbyss;
  vec3 c = mix(floorCol, uLight > 0.5 ? uBg : vec3(0.0), lines * uLines);
  c += noise * (uLight > 0.5 ? 0.025 : 0.006);

  float reveal = hover * (0.08 + 0.3 * spot + light * 1.15);
  float phase = dot(lv, vec2(0.48, -0.32)) + p.y * 0.32 + contour * 0.003;
  vec3 prl = pearl(phase);
  vec3 foil = prl + diffraction(across, lv, 1.65) * L.z * 0.5;
  c = mix(c, foil, clamp((lines * reveal + light * 0.6) * uFoil, 0.0, 1.0));
  c += (prl * 0.5 + 0.5) * glint * spot * hover * uGlint;
  c += prl * pow(max(noise + 0.5, 0.0), 24.0) * glint * spot * hover * uSparkle * 2.0;

  // Sunk into the surface, light from the top-left (uv y runs up).
  float sh = wall(vec2(-1.0, 1.0));
  float hl = wall(vec2(1.0, -1.0));
  c = mix(c, vec3(0.0), clamp(sh * uShadow, 0.0, 1.0));
  c = mix(c, uLight > 0.5 ? vec3(1.0) : uBg, clamp(hl * uBevel * 0.6, 0.0, 1.0));

  gl_FragColor = vec4(clamp(c, 0.0, 1.0) * mask, mask); // premultiplied
}`;

const vert = /* glsl */ `varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

export function initFooter(): void {
  // Phones: the 7 service lists start closed
  if (matchMedia('(max-width: 640px)').matches) {
    document.querySelectorAll('.footer-services details').forEach((d) => d.removeAttribute('open'));
  }

  const host = document.querySelector<HTMLElement>('.foil-word');
  const span = host?.querySelector('span');
  if (!host || !span) return;
  const word = span.textContent ?? '';

  // Edge to edge: scale the font so the word spans the box width.
  let lastW = 0;
  const fit = () => {
    if (host.clientWidth === lastW) return false;
    lastW = host.clientWidth;
    host.style.fontSize = '';
    host.style.fontSize = `${parseFloat(getComputedStyle(host).fontSize) * (lastW / span.getBoundingClientRect().width)}px`;
    return true;
  };
  fit();
  document.fonts.load('100px Stanley', word).then(() => { lastW = 0; fit(); });

  // Shared renderer on home; elsewhere this word is the page's only canvas.
  const shared = getWebGL().renderer;
  let renderer = shared;
  if (!renderer) {
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    } catch {
      return; // no WebGL: the DOM word stays
    }
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    host.append(renderer.domElement);
  }
  const gl = renderer;

  const maskCanvas = document.createElement('canvas');
  const mask = new THREE.CanvasTexture(maskCanvas);
  const uniforms = {
    uMask: { value: mask }, uRes: { value: new THREE.Vector2() },
    uPointer: { value: new THREE.Vector2() }, uHover: { value: 0 }, uLight: { value: 0 },
    uBg: { value: new THREE.Vector3() }, uInk: { value: new THREE.Vector3() },
    uDim: { value: TUNE.textDim }, uAbyss: { value: TUNE.abyss }, uDepth: { value: TUNE.depth },
    uShadow: { value: TUNE.shadow }, uBevel: { value: TUNE.bevel }, uLip: { value: TUNE.lip },
    uLines: { value: TUNE.lines }, uFoil: { value: TUNE.foil }, uSat: { value: TUNE.saturation },
    uGlint: { value: TUNE.glint }, uSparkle: { value: TUNE.sparkle }, uSize: { value: TUNE.lightSize },
  };
  const material = new THREE.ShaderMaterial({ vertexShader: vert, fragmentShader: frag, uniforms, transparent: true, premultipliedAlpha: true, depthTest: false });
  const scene = new THREE.Scene();
  scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));
  const camera = new THREE.Camera();
  // Token colours as raw sRGB (the shader writes them straight out).
  const root = document.documentElement;
  const tmp = new THREE.Color(), rgb = { r: 0, g: 0, b: 0 };
  const token = (name: string, out: THREE.Vector3) => {
    tmp.set(getComputedStyle(root).getPropertyValue(name).trim()).getRGB(rgb, THREE.SRGBColorSpace);
    out.set(rgb.r, rgb.g, rgb.b);
  };
  const readTheme = () => {
    token('--bg', uniforms.uBg.value); token('--fg', uniforms.uInk.value);
    uniforms.uLight.value = root.dataset.theme === 'light' ? 1 : 0;
  };
  new MutationObserver(readTheme).observe(root, { attributes: true, attributeFilter: ['data-theme'] });
  readTheme();

  const drawMask = () => {
    const w = host.clientWidth, h = host.clientHeight, dpr = gl.getPixelRatio();
    uniforms.uRes.value.set(w, h);
    if (!shared) gl.setSize(w, h, false);
    maskCanvas.width = Math.round(w * dpr); maskCanvas.height = Math.round(h * dpr);
    const ctx = maskCanvas.getContext('2d')!;
    const cs = getComputedStyle(host);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, h); // a mask, never shown
    ctx.fillStyle = '#fff';
    ctx.font = `${cs.fontWeight} ${cs.fontSize} Stanley`;
    ctx.letterSpacing = cs.letterSpacing;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(word, w / 2, h / 2);
    mask.needsUpdate = true;
  };
  new ResizeObserver(() => { fit(); drawMask(); }).observe(host);
  document.fonts.load('100px Stanley', word).then(drawMask);

  // Cursor -> word space; the reveal starts before the cursor reaches the word.
  let targetHover = 0;
  const target = new THREE.Vector2();
  addEventListener('pointermove', (e) => {
    const r = host.getBoundingClientRect();
    target.set(((e.clientX - r.left) / r.height - r.width / r.height / 2) * 1.8, -((e.clientY - r.top) / r.height - 0.5) * 1.8);
    const dx = Math.max(0, r.left - e.clientX, e.clientX - r.right);
    const dy = Math.max(0, r.top - e.clientY, e.clientY - r.bottom);
    const k = Math.max(0, 1 - Math.hypot(dx, dy) / (r.height * 0.8));
    targetHover = k * k * (3 - 2 * k);
  }, { passive: true });
  document.addEventListener('pointerleave', () => { targetHover = 0; });

  const still = matchMedia('(prefers-reduced-motion: reduce)');
  let visible = false;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(host);
  const aim = new THREE.Vector2();

  const step = () => {
    const dr = gsap.ticker.deltaRatio(60);
    const blend = still.matches ? 1 : 1 - Math.exp(-dr / 6);
    // Idle: a slow figure-eight across the whole word (sine: zero velocity at the turns).
    const t = (gsap.ticker.time * TUNE.idleSpeed), aspect = host.clientWidth / host.clientHeight;
    const near = targetHover > 0.05;
    if (near) aim.copy(target); else aim.set(Math.sin(t) * aspect * 0.95, Math.sin(t * 2) * 0.35);
    uniforms.uHover.value += (Math.max(targetHover, still.matches ? 0 : TUNE.idle) - uniforms.uHover.value) * blend;
    if (!still.matches) uniforms.uPointer.value.lerp(aim, near ? blend : blend * 0.15);
  };

  if (shared) {
    // The fixed canvas comes above the page only while the word is on screen.
    const canvas = gl.domElement;
    new IntersectionObserver(([e]) => { if (!e.isIntersecting) canvas.classList.remove('webgl--footer'); }).observe(host);
    setFooterPass(() => {
      if (!visible) return false;
      canvas.classList.add('webgl--footer');
      step();
      const r = host.getBoundingClientRect(), H = canvas.clientHeight, W = canvas.clientWidth;
      gl.setScissorTest(false);
      gl.setClearColor(0x000000, 0);
      gl.clear();
      gl.setViewport(r.left, H - r.bottom, r.width, r.height);
      gl.setScissor(r.left, H - r.bottom, r.width, r.height);
      gl.setScissorTest(true);
      gl.render(scene, camera);
      gl.setScissorTest(false);
      gl.setViewport(0, 0, W, H);
      host.classList.add('is-live');
      return true;
    });
  } else {
    gsap.ticker.add(() => {
      if (!visible) return;
      step();
      gl.render(scene, camera);
      host.classList.add('is-live');
    });
  }
}
